import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useContext, useState } from 'react';
import SnackbarContext from '../../contexts/SnackbarContext';

/**
 * The properties for the confirmation dialog
 */
interface ConfirmDialogProps {
  /**
   * The dialog title
   */
  dialogTitle: string;
  /**
   * The body text within the dialog.
   */
  dialogBody: string;
  /**
   * The message to show if the confirm action was successful.
   */
  successMessage: string;
  /**
   * The error message to show in the snackbar if the confirmation action throws an exception.
   */
  errorMessage: string;
  /**
   * Whether the dialog should be shown or not.
   */
  showDialog: boolean;
  /**
   * A method to handle closing the dialog.
   */
  onClose: () => void;
  /**
   * The text that should show on the confirmation button.
   */
  confirmText: string;
  /**
   * This action is triggered when the confirmation button is clicked.
   */
  confirmAction: () => Promise<void>;
  /**
   * (Optional) The color of the confirmation button. Defaults to primary.
   */
  confirmColor:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
    | undefined;
}

/**
 * The confirm dialog component is a standard way for us to get confirmation from the user
 *  before taking a certain action. When the confirmation is initiated, the dialog will
 *  indicate loading is occurring. If an exception is thrown when the action is taken,
 *  an error snackbar will show.
 */
const ConfirmDialog = (props: ConfirmDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const snackbar = useContext(SnackbarContext);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * Handle when the confirm button was clicked
   */
  const handleConfirm = useCallback(() => {
    setIsLoading(true);
    props
      .confirmAction()
      .then(() => {
        props.onClose();
        snackbar.setAlert(
          <Alert severity="success" variant="filled">
            {props.successMessage}
          </Alert>
        );
      })
      .catch(() => {
        snackbar.setAlert(
          <Alert severity="error" variant="filled">
            {props.errorMessage}
          </Alert>
        );
      })
      .finally(() => {
        setIsLoading(false);
        snackbar.triggerSnackbar();
      });
  }, [props, snackbar]);

  return (
    <Dialog
      open={props.showDialog}
      onClose={props.onClose}
      fullScreen={fullScreen}
    >
      <DialogTitle>{props.dialogTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText>{props.dialogBody}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          onClick={props.onClose}
          variant="outlined"
          color="secondary"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={isLoading}
          onClick={handleConfirm}
          variant="contained"
          color={props.confirmColor}
        >
          {props.confirmText}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
